/**
 * The 3D world's shaders. The geometry pass writes two buffers instead of colours, the post pass
 * turns them into paper, ink and accent the way the flat renderer's post pass does (visual spec,
 * sections Mist and Dither), and the marker pass draws numbers and banners crisp on top.
 *
 * Geometry pass, per pixel:
 *   scene: R tone (0 ink to 1 paper), G kind (0 scene, 1 accent), B height above the mist floor
 *          (0 at the floor, 1 on the plateau and above), A the mist weight of the pixel's tile.
 *   id:    RG a 16-bit outline id, B 1 where no outline is drawn (silhouettes), A coverage.
 *
 * Post pass: outlines where the id changes or the coverage ends, the mist floor found by casting
 * the pixel's ray down to the floor, the fall into the mist by height and weight, and the
 * clustered-dot halftone, turned with the world so the dots do not swim when it turns.
 */

/**
 * The core's mix32 on 32-bit unsigned integers (core spec, section 2), for what the shaders draw
 * at random: the same bits on every GPU, where the last bits of sin differ from one GPU to the
 * next and moved the smoke and the ripples (decision 0020). unitHash is its top 24 bits as a
 * float in [0, 1), exact.
 */
const HASH = /* glsl */ `
uint mix32(uint a, uint b, uint c) {
  uint h = (a * 0x27D4EB2Du) ^ (b * 0x165667B1u) ^ (c * 0x9E3779B1u);
  h = (h ^ (h >> 15u)) * 0x85EBCA6Bu;
  h = (h ^ (h >> 13u)) * 0xC2B2AE35u;
  return h ^ (h >> 16u);
}
float unitHash(uint h) {
  return float(h >> 8u) / 16777216.0;
}
`;

const SHADE = /* glsl */ `
uniform vec3 uSun;
// The band of a shaded part, its light lifted by lift before the bands cut it (a crop's billow):
// bands 0 the plain ones, 1 the conifers' darker ones, 2 a crop's lighter ones, ripe grain.
float bandToneLift(vec3 n, float bands, float lit, float lift) {
  float l = max(dot(normalize(n), uSun), 0.0) * lit;
  float v = 0.35 + 0.65 * l + lift;
  if (bands > 1.5) return v < 0.5 ? 0.62 : v < 0.68 ? 0.78 : v < 0.84 ? 0.9 : 1.0;
  if (bands > 0.5) return v < 0.5 ? 0.22 : v < 0.68 ? 0.36 : v < 0.84 ? 0.5 : 0.66;
  return v < 0.5 ? 0.4 : v < 0.68 ? 0.6 : v < 0.84 ? 0.8 : 1.0;
}
float bandTone(vec3 n, float bands, float lit) {
  return bandToneLift(n, bands, lit, 0.0);
}
`;

/**
 * Shadows from a depth map rendered from the shadow sun. The point is pushed a little along its
 * face's normal toward the light first, so faces do not shadow themselves. Four taps one texel
 * apart soften the edge; 1 is in light, 0 in shadow.
 */
const SHADOW = /* glsl */ `
uniform sampler2D uShadowMap;
uniform mat4 uShadowMatrix;
uniform vec3 uShadowDir;
uniform float uShadowTexel;
uniform float uShadowOn;
// face is the surface's normal from the caller's derivatives, taken before any branch.
float sunlit(vec3 world, vec3 face) {
  if (uShadowOn < 0.5) return 1.0;
  vec3 n = normalize(face);
  n *= sign(dot(n, uShadowDir) + 1e-6);
  vec4 lp = uShadowMatrix * vec4(world + n * 0.02, 1.0);
  vec3 c = lp.xyz / lp.w * 0.5 + 0.5;
  if (c.x <= 0.0 || c.y <= 0.0 || c.x >= 1.0 || c.y >= 1.0 || c.z >= 1.0) return 1.0;
  float lit = 0.0;
  for (int i = 0; i < 4; i++) {
    vec2 o = (vec2(float(i & 1), float(i >> 1)) - 0.5) * uShadowTexel;
    lit += c.z - 0.0008 > texture(uShadowMap, c.xy + o).r ? 0.0 : 0.25;
  }
  return lit;
}
`;

/**
 * Ripples: the plane is cut into cells of 0.7 tiles, every other row shifted by half a cell,
 * and each cell holds at most one ripple: a line about 0.53 tiles long that waves twice along
 * its length, travels 0.18 tiles across itself over its life of 5 to 8 seconds, like a small
 * wave rolling in, and fades in and out by
 * sin(pi life), its ends tapered. About half the cells carry one at a time. The line is two
 * device pixels or so wide at any zoom.
 */
const RIPPLES = /* glsl */ `
const float STIPPLE[16] = float[16](0.0, 8.0, 2.0, 10.0, 12.0, 4.0, 14.0, 6.0, 3.0, 11.0, 1.0, 9.0, 15.0, 7.0, 13.0, 5.0);
// A cell's numbers by mix32 of its corner and "ripl" plus n; a negative corner counts as its
// two's complement.
float rippleHash(vec2 c, float n) {
  return unitHash(mix32(uint(int(c.x)), uint(int(c.y)), 0x7269706Cu + uint(n)));
}
// qx and qy are the derivatives of q across and down the screen, taken before any branch.
float ripples(vec2 q, float t, vec2 qx, vec2 qy) {
  vec2 g = q / 0.7;
  g.x += 0.5 * mod(floor(g.y), 2.0);
  vec2 c = floor(g);
  vec2 f = g - c;
  if (rippleHash(c, 1.0) > 0.55) return 0.0;
  float period = 5.0 + 3.0 * rippleHash(c, 2.0);
  float life = fract(t / period + rippleHash(c, 3.0));
  float alpha = sin(3.14159 * life);
  // The line lies along x and travels across itself, toward minus y, like a small wave.
  vec2 centre = vec2(0.5, 0.62 + 0.12 * rippleHash(c, 4.0) - 0.26 * life);
  float along = (f.x - centre.x) / 0.38;
  if (abs(along) > 1.0) return 0.0;
  float wave = centre.y + 0.045 * sin(along * 6.2832 + rippleHash(c, 5.0) * 6.0);
  float d = abs(f.y - wave);
  float width = (abs(qx.y) + abs(qy.y)) / 0.7 * 1.6;
  float line = 1.0 - smoothstep(width * 0.5, width, d);
  float taper = 1.0 - along * along * along * along;
  return line * alpha * taper;
}
`;

/**
 * Rings where a fish left the water or fell back in: three circles that spread from the point at
 * 0.12 tiles a second, one after another 0.1 tiles apart, and fade over three seconds, lines
 * about three device pixels wide so they hold their own among the water's dots. Each
 * splash is its place, its age in seconds and its strength.
 */
const SPLASHES = /* glsl */ `
uniform vec4 uSplashes[16];
uniform int uSplashCount;
float splashes(vec2 q, vec2 qx, vec2 qy) {
  float best = 0.0;
  for (int i = 0; i < 16; i++) {
    if (i >= uSplashCount) break;
    vec4 s = uSplashes[i];
    vec2 v = q - s.xy;
    float d = length(v);
    vec2 n = v / max(d, 1e-6);
    float width = (abs(dot(n, qx)) + abs(dot(n, qy))) * 2.4;
    float left = 1.0 - s.z / 3.0;
    for (int k = 0; k < 3; k++) {
      float r = 0.03 + 0.12 * s.z - 0.1 * float(k);
      if (r <= 0.0) break;
      float line = 1.0 - smoothstep(width * 0.5, width, abs(d - r));
      best = max(best, line * s.w * left);
    }
  }
  return best;
}
`;

/**
 * Grass on the land tops (visual spec, Scene composition, Grass; Daniel, 2026-10-23): tufts of a
 * few blades in ink, standing up on the screen like the tufts that are models and swaying
 * faintly in their wind, at most one foot in each cell of a grid a quarter of a tile across, more
 * of them in lush patches a few tiles across, which also darken the ground a shade for the
 * halftone. Feet keep clear of a number's middle.
 */
const GRASS = /* glsl */ `
uniform sampler2D uGround;
uniform vec2 uGroundOrigin;
uniform float uGrass;
const float GRASS_CELL = 0.25;
const float GRASS_TALL = 0.12;
const float GRASS_SWAY = 0.12;
const uint GRASS_SALT = 0x67726173u;
// Byte k of a number, as a float in [0, 1).
float grassByte(uint h, uint k) {
  return float((h >> (8u * k)) & 255u) / 256.0;
}
// Smooth value noise over a lattice cell tiles apart, from mix32 at its corners.
float grassField(vec2 q, float cell, uint salt) {
  vec2 g = q / cell;
  vec2 c = floor(g);
  vec2 f = g - c;
  f = f * f * (3.0 - 2.0 * f);
  ivec2 i = ivec2(c);
  float a = unitHash(mix32(uint(i.x), uint(i.y), salt));
  float b = unitHash(mix32(uint(i.x + 1), uint(i.y), salt));
  float d = unitHash(mix32(uint(i.x), uint(i.y + 1), salt));
  float e = unitHash(mix32(uint(i.x + 1), uint(i.y + 1), salt));
  return mix(mix(a, b, f.x), mix(d, e, f.x), f.y);
}
// How lush the grass is at q, from 0 to 1: patches a few tiles across.
float lush(vec2 q) {
  return 0.65 * grassField(q, 2.6, 0x6c757368u) + 0.35 * grassField(q, 0.9, 0x6c757369u);
}
// The patches: the ground a shade darker where the grass is lush.
float grassTone(vec2 q) {
  return 1.0 - 0.22 * smoothstep(0.5, 0.75, lush(q));
}
// The scene's ground code of tile t: 0 without a top or outside the window.
int groundAt(ivec2 t) {
  ivec2 w = t - ivec2(uGroundOrigin);
  ivec2 n = textureSize(uGround, 0);
  if (w.x < 0 || w.y < 0 || w.x >= n.x || w.y >= n.y) return 0;
  return int(texelFetch(uGround, w, 0).r * 255.0 + 0.5);
}
// One tuft of cell c, n blades: s is the pixel's offset from its foot in device pixels, y up the
// screen, h its height in device pixels and sway how far its blades bend across the screen, times
// their height. 1 on a blade.
float tuft(vec2 s, float h, float sway, ivec2 c, float n, float spread) {
  for (int j = 0; j < 6; j++) {
    if (float(j) >= n) break;
    float k = float(j) / (n - 1.0) * 2.0 - 1.0;
    float jit = unitHash(mix32(uint(c.x), uint(c.y), GRASS_SALT + 2u + uint(j))) - 0.5;
    float lean = spread * k + 0.2 * jit;
    float tall = h * (1.0 - 0.35 * k * k + 0.3 * jit);
    float t = s.y / tall;
    if (t < 0.0 || t > 1.0) continue;
    float bend = 0.7 * lean + sway;
    float x = 0.12 * h * k + tall * (lean * t + bend * t * t);
    float slope = lean + 2.0 * bend * t;
    float d = abs(s.x - x) / sqrt(1.0 + slope * slope);
    if (d < mix(1.0, 0.3, t)) return 1.0;
  }
  return 0.0;
}
// The tufts' ink at ground point q; qx and qy are its derivatives across and up the screen, taken
// before any branch.
float grassTufts(vec2 q, vec2 qx, vec2 qy) {
  float px = length(qx);
  float sinT = clamp(px / length(qy), 0.0, 1.0);
  float tallest = GRASS_TALL * sqrt(1.0 - sinT * sinT) / px;
  if (tallest < 4.0) return 0.0;
  // From the plateau to device pixels: the inverse of the matrix whose columns are qx and qy.
  float det = qx.x * qy.y - qy.x * qx.y;
  mat2 toPixels = mat2(qy.y, -qx.y, -qy.x, qx.x) / det;
  // The share of the trees' wind, (0.92, 0.38) on the plateau, that blows across the screen.
  float windX = (toPixels * vec2(0.92, 0.38)).x * px;
  // The feet whose tufts reach q stand below it on the screen, within a tuft's width and height:
  // a blade grows to 1.15 times its tuft's height.
  float wide = 1.2 * tallest;
  vec2 a = q - qy * (1.2 * tallest);
  vec2 lo = min(min(q - qx * wide, q + qx * wide), min(a - qx * wide, a + qx * wide));
  vec2 hi = max(max(q - qx * wide, q + qx * wide), max(a - qx * wide, a + qx * wide));
  ivec2 c0 = ivec2(floor(lo / GRASS_CELL));
  ivec2 c1 = ivec2(floor(hi / GRASS_CELL));
  for (int cy = 0; cy < 6; cy++) {
    if (c0.y + cy > c1.y) break;
    for (int cx = 0; cx < 6; cx++) {
      if (c0.x + cx > c1.x) break;
      ivec2 c = c0 + ivec2(cx, cy);
      // One number for what every pixel asks of a cell: whether, where and how tall.
      uint h = mix32(uint(c.x), uint(c.y), GRASS_SALT);
      float r = grassByte(h, 0u);
      if (r >= 0.8) continue;
      vec2 foot = (vec2(c) + 0.1 + 0.8 * vec2(grassByte(h, 1u), grassByte(h, 2u))) * GRASS_CELL;
      float tall = tallest * (0.6 + 0.4 * grassByte(h, 3u));
      vec2 s = toPixels * (q - foot);
      if (s.y < 0.0 || s.y > 1.2 * tall || abs(s.x) > wide) continue;
      ivec2 t = ivec2(floor(foot + 0.5));
      int g = groundAt(t);
      int terrain = g & 7;
      if (terrain < 2) continue;
      if (texelFetch(uWeights, t - ivec2(uGroundOrigin), 0).r >= 0.4) continue;
      float most = terrain == 2 ? 0.72 : terrain == 3 ? 0.36 : 0.22;
      if (r >= 0.05 + most * smoothstep(0.32, 0.76, lush(foot))) continue;
      if (g >= 8 && length(foot - vec2(t)) < 0.34) continue;
      uint k = mix32(uint(c.x), uint(c.y), GRASS_SALT + 1u);
      // Zoomed out, the smallest tufts go first, whole.
      if (tall < 4.0 + 6.0 * grassByte(k, 0u)) continue;
      // Small tufts have fewer blades, so they stay open instead of closing into a blot.
      float n = clamp(floor(2.0 + tall / 7.0 + 2.0 * grassByte(k, 1u)), 3.0, 6.0);
      float spread = 0.3 + 0.25 * grassByte(k, 2u);
      // The wind as the models feel it at the tuft's foot (visual spec, Wind).
      float phi = 0.71 * foot.x + 0.53 * foot.y;
      float gust = 0.6 + 0.4 * sin(0.23 * uTime + 0.11 * foot.x - 0.07 * foot.y);
      float wave = sin(1.25 * uTime + phi) + 0.35 * sin(2.9 * uTime + 1.7 * phi);
      if (tuft(s, tall, GRASS_SWAY * windX * gust * wave, c, n, spread) > 0.5) return 1.0;
    }
  }
  return 0.0;
}
`;

/** How dark a fixed tone grows in shadow: tone times this. */
const SHADOW_TONE = "0.68";

const GBUFFER_OUT = /* glsl */ `
layout(location = 0) out vec4 gScene;
layout(location = 1) out vec4 gId;
void writeBuffers(float tone, float kind, float height, float weight, float id, float noLine) {
  gScene = vec4(tone, kind / 255.0, height, weight);
  float lo = mod(id, 256.0);
  float hi = floor(id / 256.0);
  gId = vec4(lo / 255.0, hi / 255.0, noLine, 1.0);
}
`;

export const COLUMN_VERTEX = /* glsl */ `
precision highp float;
precision highp sampler2D;
uniform mat4 modelViewMatrix;
uniform mat4 projectionMatrix;
uniform sampler2D uWeights;
uniform float uDepth;
in vec3 position;
in float aTone;
in float aId;
in vec2 aTile;
in float aKind;
out float vTone;
out float vHeight;
out float vWeight;
out vec2 vPlace;
out vec3 vWorld;
flat out float vId;
flat out float vKind;
void main() {
  float w = texelFetch(uWeights, ivec2(aTile), 0).r;
  vec3 p = position;
  vHeight = clamp((p.y + uDepth) / uDepth, 0.0, 1.0);
  vPlace = p.xz;
  p.y -= uDepth * w;
  vWorld = p;
  vTone = aTone;
  vWeight = w;
  vId = aId;
  vKind = aKind;
  gl_Position = projectionMatrix * modelViewMatrix * vec4(p, 1.0);
}
`;

export const COLUMN_FRAGMENT = /* glsl */ `
precision highp float;
precision highp sampler2D;
uniform float uTime;
uniform sampler2D uWeights;
in float vTone;
in float vHeight;
in float vWeight;
in vec2 vPlace;
in vec3 vWorld;
flat in float vId;
flat in float vKind;
${GBUFFER_OUT}
${SHADOW}
${HASH}
${RIPPLES}
${SPLASHES}
${GRASS}
void main() {
  // Derivatives first, while every pixel of a 2 by 2 block is still running: after a branch or
  // a return that only some of them take, they are undefined, and some GPUs then draw lines.
  vec3 face = cross(dFdx(vWorld), dFdy(vWorld));
  vec2 placeX = dFdx(vPlace);
  vec2 placeY = dFdy(vPlace);
  // Ripples on water: short wavy lines, one per cell of a loose grid, that drift slowly and
  // fade in and out over a few seconds each.
  // The line is crisp ink; as it fades, a fine ordered stipple in device pixels lets fewer
  // and fewer of its pixels through, so it dissolves into dashes and dots.
  if (vKind > 0.5 && vWeight < 0.4) {
    float ripple = max(ripples(vPlace, uTime, placeX, placeY), splashes(vPlace, placeX, placeY));
    ivec2 sp = ivec2(gl_FragCoord.xy) & 3;
    float stipple = (STIPPLE[sp.x + 4 * sp.y] + 0.5) / 16.0;
    if (ripple > stipple) {
      writeBuffers(0.0, 2.0, vHeight, vWeight, vId, 0.0);
      return;
    }
  }
  float tone = vTone;
  // Only the tops take shadows: the walls fall into the mist, and their tones already say which
  // way they face.
  vec3 n = normalize(face);
  bool top = abs(n.y) > 0.5;
  // Grass on the land tops: the tufts in ink, the patches a shade darker.
  if (top && vKind < 0.5 && uGrass > 0.5) {
    if (vWeight < 0.4 && grassTufts(vPlace, placeX, placeY) > 0.5) {
      writeBuffers(0.0, 2.0, vHeight, vWeight, vId, 0.0);
      return;
    }
    tone *= grassTone(vPlace);
  }
  float lit = top ? sunlit(vWorld, face) : 1.0;
  writeBuffers(tone * mix(${SHADOW_TONE}, 1.0, lit), 0.0, vHeight, vWeight, vId, 0.0);
}
`;

export const MODEL_VERTEX = /* glsl */ `
precision highp float;
precision highp sampler2D;
${HASH}
uniform mat4 modelViewMatrix;
uniform mat4 projectionMatrix;
uniform sampler2D uWeights;
uniform float uDepth;
uniform float uTime;
uniform float uCamYaw;
uniform float uBannerTop;
uniform vec4 uBannerFly;
uniform float uPennantTop;
in vec3 position;
in vec3 normal;
in float aFixed;
in float aGroup;
in mat4 instanceMatrix;
in vec2 iTile;
in float iId;
in float iFlags;
in float iSway;
in vec2 iLife;
out vec3 vNormal;
out vec3 vWorld;
out float vHeight;
out float vWeight;
flat out float vFixed;
flat out float vId;
flat out float vFlags;
flat out float vDissolve;
out float vBillow;
void main() {
  int flags = int(iFlags + 0.5);
  vDissolve = 1.0;
  vBillow = 0.0;
  bool ghost = (flags & 1) != 0;
  // A banner stands clear of its tile's mist: only its height fades it, so its pole goes down
  // into the mist on a tile still hidden.
  float w = ghost || (flags & 32) != 0 ? 0.0 : texelFetch(uWeights, ivec2(iTile), 0).r;
  // A massif stands on 2 by 2 tiles and rises with the last of them out of the mist.
  if ((flags & 512) != 0) {
    ivec2 last = textureSize(uWeights, 0) - 1;
    ivec2 t = ivec2(iTile);
    w = max(w, texelFetch(uWeights, min(t + ivec2(1, 0), last), 0).r);
    w = max(w, texelFetch(uWeights, min(t + ivec2(0, 1), last), 0).r);
    w = max(w, texelFetch(uWeights, min(t + ivec2(1, 1), last), 0).r);
  }
  // Sails turn around the model's own x axis, slowly, each mill on its own beat.
  vec3 local = position;
  vec3 localNormal = normal;
  if ((flags & 8) != 0) {
    vec3 hub = instanceMatrix[3].xyz;
    float a = uTime * 0.9 + hub.x * 1.3 + hub.z * 0.7;
    float c = cos(a);
    float s = sin(a);
    local = vec3(local.x, local.y * c - local.z * s, local.y * s + local.z * c);
    localNormal = vec3(normal.x, normal.y * c - normal.z * s, normal.y * s + normal.z * c);
  }
  // A deer's head nods around its own z axis: down to graze for most of a slow cycle, chewing a
  // little, then up to look around for a while, each deer on its own beat.
  if ((flags & 16) != 0) {
    vec3 neck = instanceMatrix[3].xyz;
    float beat = fract(neck.x * 0.37 + neck.z * 0.61);
    float cycle = fract(uTime / (11.0 + 3.0 * fract(neck.x * 0.13 + neck.z * 0.29)) + beat);
    float up = smoothstep(0.58, 0.66, cycle) * (1.0 - smoothstep(0.86, 0.94, cycle));
    float a = mix(-1.9 + 0.05 * sin(uTime * 6.5 + beat * 40.0), 0.0, up);
    float c = cos(a);
    float s = sin(a);
    local = vec3(local.x * c - local.y * s, local.x * s + local.y * c, local.z);
    localNormal = vec3(normal.x * c - normal.y * s, normal.x * s + normal.y * c, normal.z);
  }
  // A silhouette rises out of the mist: its foot, what lies at the ground in its own units, is
  // drawn down at the mist floor. Set above the plateau's top, as a rider on its host or a flat
  // thing lifted in the tray, its ground lies lower in its own units, by its height over its
  // scale, so only what reaches the ground sinks.
  if (ghost && local.y <= 0.02 - max(instanceMatrix[3].y, 0.0) / length(instanceMatrix[0].xyz)) {
    local.y = (-uDepth - instanceMatrix[3].y) / length(instanceMatrix[0].xyz);
  }
  // A duck swims, each on its own beat of P seconds: it glides a stretch of a circle round its
  // place, heading along the circle and slowing into a stop, looks to one side and back, upends
  // to feed, tipping forward about its middle at the waterline so its head goes under the water,
  // which hides it, and swims on. It is placed with no turn: its heading is this turn. The same
  // path gives the rings that spread from it (the renderer's duckAt).
  if ((flags & 2048) != 0) {
    vec3 home = instanceMatrix[3].xyz;
    float beat = fract(home.x * 0.53 + home.z * 0.29);
    float period = 20.0 + 6.0 * fract(home.x * 0.17 + home.z * 0.41);
    float cycle = uTime / period + beat;
    float k = floor(cycle);
    float u = cycle - k;
    float theta = beat * 6.2831853 + (k + smoothstep(0.0, 0.5, u)) * 2.2;
    float look = smoothstep(0.52, 0.58, u) * (1.0 - smoothstep(0.66, 0.72, u));
    float side = mod(k, 2.0) < 1.0 ? 0.9 : -0.9;
    float down = smoothstep(0.76, 0.8, u) * (1.0 - smoothstep(0.92, 0.96, u));
    float tip = down * (-1.6 + 0.06 * sin(uTime * 7.0 + beat * 30.0));
    float c = cos(tip);
    float s = sin(tip);
    local = vec3(local.x * c - local.y * s, local.x * s + local.y * c, local.z);
    localNormal = vec3(localNormal.x * c - localNormal.y * s, localNormal.x * s + localNormal.y * c, localNormal.z);
    float heading = theta + 1.5707963 + look * side;
    c = cos(heading);
    s = sin(heading);
    local = vec3(local.x * c + local.z * s, local.y, -local.x * s + local.z * c);
    localNormal = vec3(localNormal.x * c + localNormal.z * s, localNormal.y, -localNormal.x * s + localNormal.z * c);
    local += vec3(cos(theta), 0.0, -sin(theta)) * 0.16;
  }
  // A puff of smoke rises from its source, the instance's place, in turn with the others of its
  // source: it grows, turns, drifts more and more with the trees' wind as it rises, and dissolves
  // into dots near the top. Each source has its own beat and each puff its own size and drift in
  // every round, so no two columns of smoke move alike. iLife holds the puff's number among its
  // source's five and the source's size (smaller from a campfire).
  if ((flags & 4096) != 0) {
    vec3 base = instanceMatrix[3].xyz;
    float k = iLife.x;
    float size = iLife.y;
    float beat = fract(base.x * 0.37 + base.z * 0.61);
    float period = 5.5 + 2.5 * fract(base.x * 0.13 + base.z * 0.71);
    float cycle = uTime / period + k / 5.0 + beat;
    float phase = fract(cycle);
    float r = unitHash(mix32(uint(int(floor(cycle))), uint(int(k)), uint(floor(beat * 4096.0))));
    float gust = 0.6 + 0.4 * sin(uTime * 0.23 + base.x * 0.11 - base.z * 0.07);
    float radius = size * (0.11 + 0.3 * phase) * (0.8 + 0.4 * r);
    float spin = phase * 1.4 + r * 6.2831853;
    float c = cos(spin);
    float s = sin(spin);
    local = vec3(local.x * c + local.z * s, local.y, -local.x * s + local.z * c) * radius;
    localNormal = vec3(localNormal.x * c + localNormal.z * s, localNormal.y, -localNormal.x * s + localNormal.z * c);
    vec2 drift = vec2(0.92, 0.38) * size * (0.12 * phase + 0.38 * phase * phase) * gust
      + vec2(-0.38, 0.92) * size * 0.05 * sin(phase * 5.0 + r * 6.2831853);
    local += vec3(drift.x, size * (0.1 + 1.25 * phase + 0.15 * phase * phase), drift.y);
    vDissolve = smoothstep(0.0, 0.06, phase) * (1.0 - smoothstep(0.6, 1.0, phase));
  }
  // An eagle soars: the model, built with the bird out from its origin and heading round it,
  // turns about its vertical axis through its origin, a full circle in about twenty seconds,
  // each on its own beat.
  if ((flags & 1024) != 0) {
    vec3 centre = instanceMatrix[3].xyz;
    float a = uTime * 0.31 + fract(centre.x * 0.37 + centre.z * 0.61) * 6.2831853;
    float c = cos(a);
    float s = sin(a);
    local = vec3(local.x * c + local.z * s, local.y, -local.x * s + local.z * c);
    localNormal = vec3(localNormal.x * c + localNormal.z * s, localNormal.y, -localNormal.x * s + localNormal.z * c);
  }
  // A banner rises out of the mist and unrolls its cloth when placed, rolls the cloth up and
  // sinks back into the mist when taken away, and its cloth waves in the trees' wind. A creature
  // rises the same way after death, more slowly. iLife holds when it was placed and when it was
  // taken away (below 0 while it stands), in the clock's seconds. Done before it turns, while
  // its cloth still faces the default view.
  float sink = 0.0;
  if ((flags & 64) != 0) {
    float born = uTime - iLife.x;
    // A creature (misted) rises slowly out of its gap; a banner is quick.
    float rise = smoothstep(0.0, (flags & 128) != 0 ? 1.1 : 0.3, born);
    float unroll = smoothstep(0.22, 0.75, born);
    if (iLife.y >= 0.0) {
      float gone = uTime - iLife.y;
      unroll = min(unroll, 1.0 - smoothstep(0.0, 0.3, gone));
      rise = min(rise, 1.0 - smoothstep(0.25, 0.7, gone));
    }
    sink = (1.0 - rise) * (uDepth + 1.0);
    if (aFixed < -1.5) {
      vec3 outward = vec3(0.7071068, 0.0, 0.7071068);
      float drop = max(uBannerTop - local.y, 0.0);
      // Below the unrolling front the cloth is still rolled up, a small roll at the front.
      float rolled = max(drop - unroll * 0.52, 0.0);
      local.y += rolled;
      local += outward * 0.02 * clamp(rolled * 40.0, 0.0, 1.0);
      // A ripple runs down the cloth and grows toward its tails, swelled by the trees' gust. A
      // cloth fixed along its staff flies out from it instead, along uBannerFly (its direction,
      // and in w how far along it the fixed edge lies), and ripples out to its free edge.
      vec3 foot = instanceMatrix[3].xyz;
      float d = drop - rolled;
      if (dot(uBannerFly.xyz, uBannerFly.xyz) > 0.5) d = max(dot(local, uBannerFly.xyz) - uBannerFly.w, 0.0);
      float phase = foot.x * 0.71 + foot.z * 0.53;
      float gust = 0.6 + 0.4 * sin(uTime * 0.23 + foot.x * 0.11 - foot.z * 0.07);
      float wave = 0.7 * sin(uTime * 2.4 - d * 10.0 + phase) + 0.3 * sin(uTime * 3.9 - d * 17.0 + 1.7 * phase);
      local += outward * 0.2 * d * d * wave * gust;
    }
  }
  // The cloth on the home cabin's flagpole stirs in the light air the chimney smoke shows: it
  // sways a little sideways and in and out, more the further it hangs below its top.
  if ((flags & 256) != 0 && aFixed < -1.5) {
    vec3 foot = instanceMatrix[3].xyz;
    float d = max(uPennantTop - local.y, 0.0);
    float phase = foot.x * 0.71 + foot.z * 0.53;
    float gust = 0.6 + 0.4 * sin(uTime * 0.23 + foot.x * 0.11 - foot.z * 0.07);
    float sway = 0.7 * sin(uTime * 1.9 - d * 9.0 + phase) + 0.3 * sin(uTime * 3.1 - d * 15.0 + 1.7 * phase);
    local += vec3(0.7071068, 0.0, -0.7071068) * 0.16 * d * d * sway * gust;
    local += vec3(0.7071068, 0.0, 0.7071068) * 0.1 * d * d * gust * sin(uTime * 2.4 - d * 12.0 + phase);
  }
  // A banner turns about its foot to face the camera, so its cloth is never seen edge-on. It is
  // built facing the default view, 45 degrees around. So does a flagpole with its pennant.
  if ((flags & 320) != 0) {
    float a = uCamYaw - 0.7853982;
    float c = cos(a);
    float s = sin(a);
    local = vec3(local.x * c + local.z * s, local.y, -local.x * s + local.z * c);
    localNormal = vec3(localNormal.x * c + localNormal.z * s, localNormal.y, -localNormal.x * s + localNormal.z * c);
  }
  vec4 p = instanceMatrix * vec4(local, 1.0);
  p.y -= sink;
  vHeight = (flags & 4) != 0 ? 1.0 : clamp((p.y + uDepth) / uDepth, 0.0, 1.0);
  // Wind: trees bend with the square of the height above their foot, grass and reeds more
  // for their size. A slow gust, different per place, swells and fades the sway.
  if (iSway > 0.5) {
    vec3 foot = instanceMatrix[3].xyz;
    float up = max(p.y - foot.y, 0.0);
    vec2 wind = vec2(0.92, 0.38);
    if (iSway > 2.5) {
      // A crop billows: each point takes the phase and the gust of its own place, the phase
      // falling along the wind, so waves roll across a field and on into the next tile; its tone
      // lifts and sinks with them.
      float along = dot(p.xz, wind);
      float gust = 0.6 + 0.4 * sin(uTime * 0.23 + p.x * 0.11 - p.z * 0.07);
      float s = sin(uTime * 1.25 - along * 2.4) + 0.35 * sin(uTime * 2.9 - along * 4.1);
      p.xz += wind * 0.6 * up * up * gust * s;
      vBillow = s * gust * min(up / 0.15, 1.0);
    } else {
      float phase = foot.x * 0.71 + foot.z * 0.53;
      float gust = 0.6 + 0.4 * sin(uTime * 0.23 + foot.x * 0.11 - foot.z * 0.07);
      float bend = (iSway < 1.5 ? 0.009 : 0.35) * up * up;
      float s = sin(uTime * 1.25 + phase) + 0.35 * sin(uTime * 2.9 + phase * 1.7);
      p.xz += wind * bend * gust * s;
    }
  }
  p.y -= uDepth * w;
  // Smoke goes as its tile sinks into the mist.
  vDissolve *= 1.0 - smoothstep(0.3, 0.45, w);
#ifdef SHADOW_PASS
  // Silhouettes stand in the mist and cast nothing, nor does smoke.
  if (ghost || (flags & 4096) != 0) {
    gl_Position = vec4(2.0, 2.0, 2.0, 1.0);
    return;
  }
#endif
  vWorld = p.xyz;
  vNormal = mat3(instanceMatrix) * localNormal;
  vWeight = w;
  // A hem part (-4) has moved with its cloth, and is shaded like any other part from here on.
  vFixed = ghost ? 0.74 : aFixed < -3.5 ? -1.0 : aFixed;
  vId = ghost ? 0.0 : 1.0 + mod(iId * 16.0 + aGroup, 65000.0);
  vFlags = iFlags;
  gl_Position = projectionMatrix * modelViewMatrix * p;
}
`;

export const MODEL_FRAGMENT = /* glsl */ `
precision highp float;
uniform float uBands;
uniform float uGlow;
in vec3 vNormal;
in vec3 vWorld;
in float vHeight;
in float vWeight;
flat in float vFixed;
flat in float vId;
flat in float vFlags;
flat in float vDissolve;
in float vBillow;
${SHADE}
${GBUFFER_OUT}
${SHADOW}
// A 4 by 4 ordered stipple in device pixels, plus one half, over 16: what fades, a puff of smoke,
// is drawn only where its strength exceeds it, so it dissolves into dots.
const float STIPPLE[16] = float[16](0.0, 8.0, 2.0, 10.0, 12.0, 4.0, 14.0, 6.0, 3.0, 11.0, 1.0, 9.0, 15.0, 7.0, 13.0, 5.0);
void main() {
  if (vDissolve < 1.0) {
    ivec2 q = ivec2(gl_FragCoord.xy) & 3;
    if (vDissolve <= (STIPPLE[q.x + 4 * q.y] + 0.5) / 16.0) discard;
  }
  vec3 face = cross(dFdx(vWorld), dFdy(vWorld));
  int flags = int(vFlags + 0.5);
  float lit = (flags & 1) != 0 ? 1.0 : sunlit(vWorld, face);
  // A part named accent- (vFixed -2) is drawn in the accent role, like the chasm that ended a run;
  // a part named glow- (vFixed -3), a house's interior seen through its openings, has the tone
  // uGlow, dark by day.
  bool glow = vFixed < -2.5;
  bool accent = !glow && vFixed < -1.5;
  float tone = glow ? uGlow
    : accent ? 0.0
    : vFixed >= 0.0 ? vFixed * mix(${SHADOW_TONE}, 1.0, lit)
    : bandToneLift(vNormal, uBands, lit, 0.08 * vBillow);
  // Smoke is light, paper in the sun and a few dots on its shaded side, and takes no shadows.
  if ((flags & 4096) != 0) tone = 0.8 + 0.2 * bandTone(vNormal, 0.0, 1.0);
  // Kind 4: an interior, whose dark dots are ink even where the theme has a shade, so at night
  // the openings shine.
  float kind = accent ? 3.0 : glow ? 4.0 : ((flags & 2) != 0 && tone < 0.15) ? 1.0 : 0.0;
  // A creature rising out of its gap fades into the mist below the plateau's top, gone half a
  // tile down, where it ends: lower down it could reach below the mist floor.
  if ((flags & 128) != 0 && vWorld.y < -0.55) discard;
  float weight = (flags & 128) != 0 ? max(vWeight, clamp(-vWorld.y / 0.5, 0.0, 1.0)) : vWeight;
  // A puff of smoke as it forms and dissolves draws no outline and takes the highest id, so the
  // holes of its stipple draw no lines on either side and it fades light.
  bool fading = (flags & 4096) != 0 && vDissolve < 0.999;
  writeBuffers(tone, kind, vHeight, weight, fading ? 65535.0 : vId, (flags & 1) != 0 || fading ? 1.0 : 0.0);
}
`;

/**
 * Flat marks on the plateau drawn in the geometry pass so the world hides them where it stands in
 * front: the keyboard cursor (kind 2, ink) and the rim of the chasm that ended the run (kind 1,
 * accent). They draw no outline and carry the highest id, so nothing outlines against them.
 */
export const OVERLAY_VERTEX = /* glsl */ `
precision highp float;
uniform mat4 modelViewMatrix;
uniform mat4 projectionMatrix;
in vec3 position;
in float aKind;
flat out float vKind;
void main() {
  vKind = aKind;
  gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
}
`;

export const OVERLAY_FRAGMENT = /* glsl */ `
precision highp float;
flat in float vKind;
${GBUFFER_OUT}
void main() {
  writeBuffers(0.0, vKind, 1.0, 0.0, 65535.0, 1.0);
}
`;

/**
 * Gnats: round dots of ink a few device pixels across, standing in the world so what
 * stands in front of a swarm hides it. Like the marks they draw no outline and carry the highest
 * id.
 */
export const GNAT_VERTEX = /* glsl */ `
precision highp float;
uniform mat4 modelViewMatrix;
uniform mat4 projectionMatrix;
uniform vec2 uViewport;
uniform float uSize;
in vec3 position;      // corner, minus 1 or 1 on x and y
in vec3 iPos;
out vec2 vCorner;
void main() {
  vec4 c = projectionMatrix * modelViewMatrix * vec4(iPos, 1.0);
  c.xy += position.xy * uSize / uViewport * c.w;
  vCorner = position.xy;
  gl_Position = c;
}
`;

export const GNAT_FRAGMENT = /* glsl */ `
precision highp float;
in vec2 vCorner;
${GBUFFER_OUT}
void main() {
  if (dot(vCorner, vCorner) > 1.0) discard;
  writeBuffers(0.0, 2.0, 1.0, 0.0, 65535.0, 1.0);
}
`;

/**
 * A fish in the air, leaping: seen from the side, facing the screen at its place in the world and
 * turned along its path on the screen, so what stands in front of it hides it and the water hides
 * it as it goes in. Paper with an ink rim, like the deer.
 */
export const FISH_VERTEX = /* glsl */ `
precision highp float;
uniform mat4 modelViewMatrix;
uniform mat4 projectionMatrix;
uniform vec2 uViewport;
uniform float uLength;
in vec3 position;      // corner, minus 1 or 1 on x and y
in vec4 iFish;         // its place in the world, and the angle of its body on the screen, y up
out vec2 vLocal;
void main() {
  vec4 c = projectionMatrix * modelViewMatrix * vec4(iFish.xyz, 1.0);
  vec2 along = vec2(cos(iFish.w), sin(iFish.w));
  vec2 across = vec2(-along.y, along.x);
  vec2 off = (along * position.x + across * position.y * 0.45) * 0.5 * uLength;
  c.xy += off / uViewport * 2.0 * c.w;
  vLocal = position.xy;
  gl_Position = c;
}
`;

export const FISH_FRAGMENT = /* glsl */ `
precision highp float;
uniform float uLength;
in vec2 vLocal;
${GBUFFER_OUT}
// The fish, its nose toward plus x: a body like a long drop and a tail fin that fans out behind
// it. True inside, shrunk by an inset in device pixels.
bool fish(vec2 l, float inset) {
  float ix = inset * 2.0 / uLength;
  float iy = inset * 2.0 / (0.45 * uLength);
  vec2 b = vec2((l.x - 0.15) / (0.8 - ix), l.y / (0.7 - iy));
  if (dot(b, b) < 1.0) return true;
  float t = -l.x - 0.55;
  return t > 0.5 * ix && t < 0.43 - ix && abs(l.y) < 0.2 + 1.6 * t - iy;
}
void main() {
  if (!fish(vLocal, 0.0)) discard;
  if (fish(vLocal, 2.0)) writeBuffers(1.0, 0.0, 1.0, 0.0, 65535.0, 1.0);
  else writeBuffers(0.0, 2.0, 1.0, 0.0, 65535.0, 1.0);
}
`;

/** The shadow pass writes depth only; its colour is thrown away. */
export const SHADOW_FRAGMENT = /* glsl */ `
precision highp float;
out vec4 outColor;
void main() {
  outColor = vec4(1.0);
}
`;

export const POST_VERTEX = /* glsl */ `
in vec3 position;
void main() {
  gl_Position = vec4(position.xy, 0.0, 1.0);
}
`;

/** The halftone's two clustered-dot screens, shared by the post pass and the tilt-shift pass. */
const SCREENS = /* glsl */ `
// The finer clustered-dot screen, dots 4 cells apart, for a zoomed-out view.
const float DITHER4[16] = float[16](
  12.0, 5.0, 6.0, 13.0,
  4.0, 0.0, 1.0, 7.0,
  11.0, 3.0, 2.0, 8.0,
  15.0, 10.0, 9.0, 14.0
);

const float DITHER[64] = float[64](
  60.0, 52.0, 40.0, 32.0, 33.0, 41.0, 53.0, 61.0,
  54.0, 48.0, 24.0, 16.0, 17.0, 25.0, 49.0, 55.0,
  42.0, 26.0, 12.0, 4.0, 5.0, 13.0, 27.0, 43.0,
  34.0, 18.0, 6.0, 0.0, 1.0, 7.0, 19.0, 35.0,
  36.0, 20.0, 8.0, 2.0, 3.0, 9.0, 21.0, 37.0,
  44.0, 28.0, 14.0, 10.0, 11.0, 15.0, 29.0, 45.0,
  56.0, 50.0, 30.0, 22.0, 23.0, 31.0, 51.0, 57.0,
  62.0, 58.0, 46.0, 38.0, 39.0, 47.0, 59.0, 63.0
);
`;

export const POST_FRAGMENT = /* glsl */ `
precision highp float;
precision highp int;
precision highp sampler2D;

uniform sampler2D uScene;
uniform sampler2D uIds;
uniform sampler2D uWeights;    // R8, one texel per tile of the window: mist weight
uniform sampler2D uNear;       // R8, one texel per tile of the window: nearness to revealed ground
uniform sampler2D uNoise;
uniform vec2 uViewport;
uniform mat4 uInvViewProj;
uniform vec3 uCamDir;
uniform float uDepth;
uniform ivec2 uWinOrigin;
uniform int uWinSize;
uniform float uArt;            // art units per tile, so the clouds keep their flat-renderer size
uniform float uTime;
uniform float uDrift;
uniform float uDot;
uniform float uFine;          // 0 the 8 by 8 screen, 1 the finer 4 by 4 one, between them a blend
uniform int uLine;             // outline width in device pixels
uniform vec2 uAnchor;          // device pixel (GL convention) of the world's origin
uniform vec2 uTurn;            // cos and sin of the angle the dots turn by
uniform vec3 uPaper;
uniform vec3 uInk;
uniform vec3 uShade;           // the dark dots of ground, walls and objects; ink unless the theme has its own
uniform vec3 uLineColour;      // the outlines, the grass and the ripples; ink unless the theme has its own
uniform vec3 uAccent;
uniform vec3 uFogParams;

out vec4 outColor;

// The colours a pixel ends in, or under TILT_PREPASS its tone, ink share, accent and line share
// for the tilt-shift pass, which blurs them before the halftone.
#ifdef TILT_PREPASS
#define OUT_ACCENT outColor = vec4(1.0, 0.0, 1.0, 0.0)
#define OUT_LINE outColor = vec4(0.0, 1.0, 0.0, 1.0)
#else
#define OUT_ACCENT outColor = vec4(uAccent, 1.0)
#define OUT_LINE outColor = vec4(uLineColour, 1.0)
#endif

${SCREENS}

float tileTexel(sampler2D tex, ivec2 tile, float outside) {
  ivec2 i = tile - uWinOrigin;
  if (i.x < 0 || i.y < 0 || i.x >= uWinSize || i.y >= uWinSize) return outside;
  return texelFetch(tex, i, 0).r;
}

float tileBilinear(sampler2D tex, vec2 t, float outside) {
  vec2 f = floor(t);
  vec2 r = t - f;
  ivec2 i = ivec2(f);
  float a = tileTexel(tex, i, outside);
  float b = tileTexel(tex, i + ivec2(1, 0), outside);
  float c = tileTexel(tex, i + ivec2(0, 1), outside);
  float d = tileTexel(tex, i + ivec2(1, 1), outside);
  return mix(mix(a, b, r.x), mix(c, d, r.x), r.y);
}

float decodeId(vec4 v) {
  return floor(v.r * 255.0 + 0.5) + 256.0 * floor(v.g * 255.0 + 0.5);
}

void main() {
  ivec2 px = ivec2(gl_FragCoord.xy);
  ivec2 size = ivec2(uViewport);
  vec4 s = texelFetch(uScene, px, 0);
  vec4 idv = texelFetch(uIds, px, 0);
  bool covered = idv.a > 0.5;
  int kind = int(s.g * 255.0 + 0.5);
  if (covered && kind == 1) { OUT_ACCENT; return; }
  if (covered && kind == 2) { OUT_LINE; return; }
  bool line = false;
  if (covered && idv.b < 0.5) {
    float id = decodeId(idv);
    for (int k = 1; k <= 4; k++) {
      if (k > uLine || line) break;
      ivec2 offs[4] = ivec2[4](ivec2(k, 0), ivec2(-k, 0), ivec2(0, k), ivec2(0, -k));
      for (int d = 0; d < 4; d++) {
        ivec2 q = clamp(px + offs[d], ivec2(0), size - 1);
        vec4 n = texelFetch(uIds, q, 0);
        if (n.a < 0.5) { line = true; break; }
        float nid = decodeId(n);
        if (nid != id && id > nid) { line = true; break; }
      }
    }
  }
  // An accent part of a model (a banner's cloth) is outlined like every other part.
  if (covered && kind == 3) {
    if (line) { OUT_LINE; } else { OUT_ACCENT; }
    return;
  }

  // An outline where the coverage ends or one outline id meets another, drawn on one side only.


  // The mist floor behind this pixel: its ray, cast down to the floor.
  vec2 ndc = gl_FragCoord.xy / uViewport * 2.0 - 1.0;
  vec4 o = uInvViewProj * vec4(ndc, -1.0, 1.0);
  o /= o.w;
  float t = (-uDepth - o.y) / uCamDir.y;
  vec2 floorTile = (o.xyz + t * uCamDir).xz;
  ivec2 tile = ivec2(floor(floorTile + 0.5));
  float cover = tileTexel(uWeights, tile, 1.0);
  float near = tileBilinear(uNear, floorTile, 0.0);
  if (covered) {
    // Geometry low in the mist meets the floor at its own base, not where its ray lands behind
    // it, which may be under the plateau: there the mist is as dense as next to a wall.
    near = max(near, 1.0 - cover);
    cover = 1.0;
  }
  float density = 0.0;
  if (cover > 0.0) {
    vec2 np = floorTile * uArt + vec2(2.0, 1.1) * uTime * uDrift;
    float n = 0.62 * texture(uNoise, np / 520.0).r + 0.38 * texture(uNoise, np / 190.0).r;
    float reach = 0.45 + 0.55 * near;
    float floorDensity = (uFogParams.x + uFogParams.y * (n - 0.5)) * reach + uFogParams.z * near;
    density = cover * clamp(floorDensity, 0.0, 1.0);
  }
  float fogTone = 1.0 - density;

  float tone = fogTone;
  // The part of the darkness that is mist, for the colour of the dark dots below.
  float misty = 1.0;
  if (covered) {
    float tt = clamp((0.55 - s.b) / 0.5, 0.0, 1.0);
    float fall = max(tt * tt * (3.0 - 2.0 * tt), s.a);
    if (line && fall < 0.4) { OUT_LINE; return; }
    tone = mix(line ? 0.0 : s.r, fogTone, fall);
    misty = tone < 1.0 ? fall * density / (1.0 - tone) : 0.0;
  }

#ifdef TILT_PREPASS
  // For the tilt-shift pass: the tone before the halftone, how much of the darkness is ink, no
  // accent, and whether it is a line's.
  outColor = vec4(tone, line || (covered && kind == 4) ? 1.0 : clamp(misty, 0.0, 1.0), 0.0, line ? 1.0 : 0.0);
  return;
#endif
  vec2 d = gl_FragCoord.xy - uAnchor;
  vec2 pc = vec2(uTurn.x * d.x + uTurn.y * d.y, -uTurn.y * d.x + uTurn.x * d.y);
  pc.y = -pc.y;
  ivec2 cell = ivec2(floor(pc / uDot));
  float threshold = (DITHER[(cell.x & 7) + 8 * (cell.y & 7)] + 0.5) / 64.0;
  if (uFine > 0.0) {
    float fine = (DITHER4[(cell.x & 3) + 4 * (cell.y & 3)] + 0.5) / 16.0;
    threshold = mix(threshold, fine, uFine);
  }
  if (tone > threshold) { outColor = vec4(uPaper, 1.0); return; }
  // A dark dot is a line's colour on a line, ink where it belongs to the mist or a lit opening, and
  // shade elsewhere: at night the shadows stay dark under light lines and a light mist. Where the
  // ground sinks into the mist ink and shade mix through a second screen, offset from the first.
  // Without a shade or lines of its own a theme draws them in ink.
  float pick = (DITHER[((cell.x + 4) & 7) + 8 * ((cell.y + 4) & 7)] + 0.5) / 64.0;
  bool inked = (covered && kind == 4) || misty > pick;
  outColor = vec4(line ? uLineColour : inked ? uInk : uShade, 1.0);
}
`;

export const MARKER_VERTEX = /* glsl */ `
precision highp float;
uniform vec2 uViewport;
in vec3 position;      // corner, 0 or 1 on x and y
in vec4 iBox;          // device-pixel box: x, y (top-left, y down), w, h
in vec4 iUv;           // u0, v0, u1, v1 in the atlas
in float iMode;        // 2 ink, 3 paper, 4 accent, 6 and 7 plus a pose below 1 a bird and its
                       // shadow
in float iFade;        // how much of a bird shows, 1 all of it, through a fine stipple
in vec3 iDepth;        // the depth at the anchor, its fall per device pixel up, the
                       // anchor's device y; a depth below 0 is never hidden
out vec2 vUv;
flat out float vMode;
flat out float vFade;
flat out vec3 vDepth;
void main() {
  vec2 dev = iBox.xy + position.xy * iBox.zw;
  vec2 clip = dev / uViewport * 2.0 - 1.0;
  gl_Position = vec4(clip.x, -clip.y, 0.0, 1.0);
  vUv = mix(iUv.xy, iUv.zw, position.xy);
  vMode = iMode;
  vFade = iFade;
  vDepth = iDepth;
}
`;

export const MARKER_FRAGMENT = /* glsl */ `
precision highp float;
uniform sampler2D uAtlas;
uniform vec3 uPaper;
uniform vec3 uInk;
uniform vec3 uShade;
uniform vec3 uAccent;
uniform vec2 uViewport;
uniform sampler2D uSceneDepth;
in vec2 vUv;
flat in float vMode;
flat in float vFade;
flat in vec3 vDepth;
out vec4 outColor;
const float STIPPLE[16] = float[16](0.0, 8.0, 2.0, 10.0, 12.0, 4.0, 14.0, 6.0, 3.0, 11.0, 1.0, 9.0, 15.0, 7.0, 13.0, 5.0);
// A bird seen from afar: two arched wings meeting at the body, their tips raised or lowered by
// the pose, 0 wings down to 1 wings up. The stroke is about two device pixels wide, more when
// zoomed in, thinning toward the tips. Returns the distance in device pixels outside the stroke,
// negative inside it.
// fw is fwidth of d, taken before any branch or discard.
float bird(vec2 d, float pose, float widen, vec2 fw) {
  vec2 p = vec2(d.x, -d.y);
  float u = abs(p.x);
  if (u > 1.0) return 1e3;
  float k = pose * 2.0 - 1.0;
  float lift = 0.55 * k;
  float arch = 0.32 - 0.12 * k;
  float y = arch * sin(3.14159 * u) + lift * u - 0.12;
  float slope = arch * 3.14159 * cos(3.14159 * u) + lift;
  // A box lower than it is wide flattens the shape; the stroke keeps its width on screen.
  float squash = fw.x / fw.y;
  float dist = abs(p.y - y) * squash / sqrt(1.0 + slope * slope * squash * squash);
  float px = fw.x;
  float width = clamp(0.18 / px, 2.0, 4.0) * widen;
  return dist / px - 0.5 * width * (1.0 - 0.45 * u * u);
}
void main() {
  // Derivatives first, while every pixel of a 2 by 2 block is still running: after a branch or
  // a discard that only some of them take, they are undefined, and some GPUs drew lines down the
  // edges of the birds' boxes, where the pixels beyond a wing's tip left early.
  vec2 box = vUv * 2.0 - 1.0;
  vec2 fw = fwidth(box);
  float fr = fwidth(length(box));
  // Decision 0010: a number or a banner is hidden where the world stands in front of it. Its depth is
  // its anchor's, less a fall per device pixel above the anchor for a banner standing up.
  if (vDepth.x >= 0.0) {
    float up = max(vDepth.z - (uViewport.y - gl_FragCoord.y), 0.0);
    if (texelFetch(uSceneDepth, ivec2(gl_FragCoord.xy), 0).r < vDepth.x - up * vDepth.y) discard;
  }
  float m = floor(vMode + 0.001);
  int mode = int(m);
  if (mode == 6) {
    // A bird: an ink stroke with a paper halo a pixel and a half wide, like the digits', so it
    // shows over trees and walls alike. Over the mist it dissolves into dots, as smoke does.
    float e = bird(box, vMode - m, 1.0, fw);
    if (e > 1.5) discard;
    ivec2 sp = ivec2(gl_FragCoord.xy) & 3;
    if (vFade <= (STIPPLE[sp.x + 4 * sp.y] + 0.5) / 16.0) discard;
    outColor = vec4(e > 0.0 ? uPaper : uInk, 1.0);
    return;
  }
  if (mode == 7) {
    // Its shadow is the same shape, smaller and flattened onto the ground by its box, with a
    // broader stroke let through half a fine stipple, so it reads as dots on the ground like the
    // world's other shadows.
    if (bird(box, vMode - m, 2.2, fw) > 0.0) discard;
    ivec2 sp = ivec2(gl_FragCoord.xy) & 3;
    if (STIPPLE[sp.x + 4 * sp.y] >= 8.0) discard;
    outColor = vec4(uShade, 1.0);
    return;
  }
  vec4 s = texture(uAtlas, vUv);
  if (s.a < 0.5) discard;
  outColor = vec4(mode == 3 ? uPaper : mode == 4 ? uAccent : uInk, 1.0);
}
`;

/**
 * Tilt-shift, a trial (Daniel, 2026-10-20): the post pass writes its tone before the halftone (and
 * how much of the darkness is ink, and the accent) to a texture, and this pass blurs it, more the
 * further a pixel lies above or below the band across the screen's middle that stays sharp, then
 * lays the same halftone over it. The world goes soft as in a photograph of a model, while the dots
 * stay crisp and the world stays paper, ink and accent; the numbers are drawn after it, sharp.
 */
export const TILT_FRAGMENT = /* glsl */ `
precision highp float;
precision highp int;
precision highp sampler2D;

uniform sampler2D uTilt;
uniform vec2 uViewport;
uniform float uBlur;           // the blur's radius at the screen's top and bottom, device pixels
uniform vec4 uTaps[24];        // per tap: the cosine and sine of its angle and its root (TILT_TAPS)
uniform vec2 uFocus;           // the sharp band: its middle and half its height, shares of the screen
uniform float uDot;
uniform float uFine;
uniform vec2 uAnchor;
uniform vec2 uTurn;
uniform vec3 uPaper;
uniform vec3 uInk;
uniform vec3 uShade;
uniform vec3 uLineColour;
uniform vec3 uAccent;

out vec4 outColor;

${SCREENS}

void main() {
  vec2 frag = gl_FragCoord.xy;
  float off = abs(frag.y / uViewport.y - uFocus.x);
  float k = smoothstep(uFocus.y, 0.5, off);
  float r = uBlur * k * k;
  vec4 v = texelFetch(uTilt, ivec2(frag), 0);
  if (r >= 1.0) {
    // A disc of taps along a golden-angle spiral, the centre weighed in too.
    vec4 sum = v;
    float n = 1.0;
    for (int i = 0; i < 24; i++) {
      vec4 t = uTaps[i];
      vec2 o = t.xy * r * t.z;
      ivec2 q = clamp(ivec2(frag + o), ivec2(0), ivec2(uViewport) - 1);
      sum += texelFetch(uTilt, q, 0);
      n += 1.0;
    }
    v = sum / n;
  }
  vec2 d = frag - uAnchor;
  vec2 pc = vec2(uTurn.x * d.x + uTurn.y * d.y, -uTurn.y * d.x + uTurn.x * d.y);
  pc.y = -pc.y;
  ivec2 cell = ivec2(floor(pc / uDot));
  float threshold = (DITHER[(cell.x & 7) + 8 * (cell.y & 7)] + 0.5) / 64.0;
  if (uFine > 0.0) {
    float fine = (DITHER4[(cell.x & 3) + 4 * (cell.y & 3)] + 0.5) / 16.0;
    threshold = mix(threshold, fine, uFine);
  }
  if (v.b > threshold) { outColor = vec4(uAccent, 1.0); return; }
  if (v.r > threshold) { outColor = vec4(uPaper, 1.0); return; }
  float pick = (DITHER[((cell.x + 4) & 7) + 8 * ((cell.y + 4) & 7)] + 0.5) / 64.0;
  outColor = vec4(v.a > pick ? uLineColour : v.g > pick ? uInk : uShade, 1.0);
}
`;
